/**
 * Project list: a tree (projects with subprojects), filtered through the
 * URL so dashboard links like `/projects?status=active` land filtered.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DataTable } from "@primereact/ui/datatable";
import { Paginator } from "@primereact/ui/paginator";
import { PROJECT_STATUSES } from "@offertli/shared";
import { StatusBadge } from "../../components/StatusBadge.js";
import { Button } from "../../components/ui/Button.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import {
  FilterTabs,
  SearchBox,
  SelectInput,
  useUrlFilters,
} from "../../components/ui/controls.js";
import { apiGet } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { ErrorPanel, LoadingPanel, formatDate } from "../shared.js";
import type { ProjectListData, ProjectListPage } from "./types.js";

const PAGE_SIZE = 25;
type SortField = "number" | "status" | "plannedStart" | "updatedAt";

function HoursBar({ planned, actual }: { planned: number; actual: number }) {
  const ratio = planned ? actual / planned : 0;
  return (
    <div className="min-w-28">
      <span className="text-xs tabular-nums text-app-muted">
        {Math.round(actual / 60)}h / {Math.round(planned / 60)}h
      </span>
      {planned ? (
        <span className="mt-1 block h-1 overflow-hidden rounded bg-app-background">
          <span
            className={`block h-full ${ratio > 1 ? "bg-danger" : "bg-info"}`}
            style={{ width: `${Math.min(100, ratio * 100)}%` }}
          />
        </span>
      ) : null}
    </div>
  );
}

export function ProjectsListPage() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const { values, page, set } = useUrlFilters([
    "q",
    "status",
    "customer",
    "member",
    "sort",
  ] as const);
  const [sortField, sortOrder] = (values.sort || "updatedAt:-1").split(":") as [
    SortField,
    string,
  ];
  const projects = useQuery({
    queryKey: ["projects", values, page],
    queryFn: () =>
      apiGet<ProjectListPage>("/projects", {
        query: {
          q: values.q || undefined,
          status: values.status || undefined,
          customerId: values.customer || undefined,
          memberId: values.member || undefined,
          page,
          pageSize: PAGE_SIZE,
          sortField,
          sortOrder,
        },
      }),
    placeholderData: (previous) => previous,
  });
  const sortBy = (field: SortField) =>
    set({
      sort: `${field}:${sortField === field && sortOrder === "1" ? -1 : 1}`,
    });
  const sortHeader = (field: SortField, label: string) => (
    <button
      type="button"
      className="font-semibold"
      onClick={() => sortBy(field)}
      aria-sort={
        sortField === field
          ? sortOrder === "1"
            ? "ascending"
            : "descending"
          : undefined
      }
    >
      {label}
      {sortField === field ? (sortOrder === "1" ? " ↑" : " ↓") : ""}
    </button>
  );

  return (
    <>
      <PageHeader
        title={t("projects.title")}
        subtitle={t("projects.subtitle")}
        actions={
          has("projects.create") ? (
            <Button as={Link} to="/projects/new" icon="pi pi-plus">
              {t("projects.new")}
            </Button>
          ) : undefined
        }
      />
      <div className="mb-3">
        <FilterTabs
          value={values.status}
          onChange={(status) => set({ status })}
          options={[
            { value: "", label: t("common.all") },
            ...PROJECT_STATUSES.map((status) => ({
              value: status,
              label: t(`status.${status}`),
            })),
          ]}
        />
      </div>
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SearchBox
          value={values.q}
          onChange={(q) => set({ q })}
          placeholder={t("projects.search")}
        />
        <SelectInput
          ariaLabel={t("nav.customers")}
          value={values.customer}
          onChange={(customer) => set({ customer })}
          placeholder={t("projects.allCustomers")}
          filter
          options={[
            { value: "", label: t("projects.allCustomers") },
            ...(projects.data?.facets.customers ?? []).map((customer) => ({
              value: customer.id,
              label: customer.label,
            })),
          ]}
        />
        <SelectInput
          ariaLabel={t("projects.members")}
          value={values.member}
          onChange={(member) => set({ member })}
          placeholder={t("projects.allMembers")}
          filter
          options={[
            { value: "", label: t("projects.allMembers") },
            ...(projects.data?.facets.members ?? []).map((member) => ({
              value: member.id,
              label: member.label,
            })),
          ]}
        />
      </div>
      {projects.isLoading ? (
        <LoadingPanel />
      ) : projects.error ? (
        <ErrorPanel error={projects.error} />
      ) : (
        <div className="rounded-xl border border-app-border bg-app-surface">
          <DataTable.Root
            data={projects.data?.data ?? []}
            dataKey="id"
            treeMode
            rowHover
          >
            <DataTable.TableContainer>
              <DataTable.Table>
                <DataTable.THead>
                  <DataTable.Row>
                    <DataTable.THeadCell className="sticky left-0 z-10 bg-app-surface">
                      {sortHeader("number", t("projects.project"))}
                    </DataTable.THeadCell>
                    <DataTable.THeadCell>
                      {sortHeader("status", t("common.status"))}
                    </DataTable.THeadCell>
                    <DataTable.THeadCell className="hidden md:table-cell">
                      {t("projects.managers")}
                    </DataTable.THeadCell>
                    <DataTable.THeadCell className="hidden lg:table-cell">
                      {sortHeader("plannedStart", t("projects.schedule"))}
                    </DataTable.THeadCell>
                    <DataTable.THeadCell className="hidden sm:table-cell">
                      {t("projects.hours")}
                    </DataTable.THeadCell>
                    <DataTable.THeadCell className="hidden xl:table-cell">
                      {sortHeader("updatedAt", t("common.updated"))}
                    </DataTable.THeadCell>
                  </DataTable.Row>
                </DataTable.THead>
                <DataTable.TBody className="divide-y divide-app-border">
                  {({ item }: { item: Record<string, unknown> }) => {
                    const project = item as ProjectListData & {
                      _treeLevel?: number;
                    };
                    return (
                      <DataTable.Row>
                        <DataTable.Cell className="sticky left-0 z-[1] bg-app-surface">
                          <div
                            className="flex min-w-56 items-start gap-2"
                            style={{
                              paddingInlineStart: `${(project._treeLevel ?? 0) * 1.25}rem`,
                            }}
                          >
                            <DataTable.RowToggle
                              aria-label={t("projects.toggleChildren")}
                            >
                              <DataTable.RowToggleIndicator match="expanded">
                                <i className="pi pi-chevron-down" />
                              </DataTable.RowToggleIndicator>
                              <DataTable.RowToggleIndicator match="collapsed">
                                <i className="pi pi-chevron-right" />
                              </DataTable.RowToggleIndicator>
                            </DataTable.RowToggle>
                            <span className="min-w-0">
                              {project.contextOnly ? (
                                <span className="block font-medium text-app-muted">
                                  {project.title}
                                </span>
                              ) : (
                                <Link
                                  className="block font-medium text-brand hover:underline"
                                  to={`/projects/${project.id}`}
                                >
                                  {project.title}
                                </Link>
                              )}
                              <span className="block text-xs text-app-muted">
                                {project.number} ·{" "}
                                {project.customer.displayName}
                              </span>
                            </span>
                          </div>
                        </DataTable.Cell>
                        <DataTable.Cell>
                          <StatusBadge status={project.status} />
                        </DataTable.Cell>
                        <DataTable.Cell className="hidden md:table-cell">
                          {project.managers
                            .map(
                              (manager) =>
                                `${manager.firstName} ${manager.lastName}`,
                            )
                            .join(", ") || "—"}
                        </DataTable.Cell>
                        <DataTable.Cell className="hidden whitespace-nowrap lg:table-cell">
                          {project.plannedStart || project.plannedEnd
                            ? `${formatDate(project.plannedStart) || "…"} – ${formatDate(project.plannedEnd) || "…"}`
                            : "—"}
                        </DataTable.Cell>
                        <DataTable.Cell className="hidden sm:table-cell">
                          <HoursBar
                            planned={project.plannedMinutes}
                            actual={project.actualMinutes ?? 0}
                          />
                        </DataTable.Cell>
                        <DataTable.Cell className="hidden whitespace-nowrap xl:table-cell">
                          {formatDate(project.updatedAt)}
                        </DataTable.Cell>
                      </DataTable.Row>
                    );
                  }}
                </DataTable.TBody>
                <DataTable.EmptyTBody>
                  <DataTable.Row>
                    <DataTable.Cell
                      colSpan={6}
                      className="py-10 text-center text-app-muted"
                    >
                      {t("projects.empty")}
                    </DataTable.Cell>
                  </DataTable.Row>
                </DataTable.EmptyTBody>
              </DataTable.Table>
            </DataTable.TableContainer>
          </DataTable.Root>
          {(projects.data?.total ?? 0) > PAGE_SIZE ? (
            <Paginator.Root
              className="border-t border-app-border py-2"
              page={page}
              total={projects.data?.total ?? 0}
              itemsPerPage={PAGE_SIZE}
              onPageChange={(event: { value: number }) =>
                set({ page: event.value })
              }
            />
          ) : null}
        </div>
      )}
    </>
  );
}
